import React from 'react';
import { Star, Quote, Users, Award, ShieldCheck, Handshake } from 'lucide-react';
import { Card } from './ui/card';

const reviews = [
  {
    id: 1,
    name: 'Michael S.',
    rating: 5,
    text: 'Hervorragende Beratung beim Kauf meines Mazda CX-5. Das Team hat sich viel Zeit genommen und alle meine Fragen beantwortet.',
    date: 'November 2025'
  },
  {
    id: 2,
    name: 'Sandra K.',
    rating: 5,
    text: 'Werkstatt-Service ist top! Schnelle Terminvergabe, faire Preise und die Arbeit wird immer sauber erledigt.',
    date: 'Oktober 2025'
  },
  {
    id: 3,
    name: 'Thomas R.',
    rating: 5,
    text: 'Seit über 10 Jahren Kunde bei Autohaus Wilke. Zuverlässig, kompetent und immer freundlich. Kann ich nur empfehlen!',
    date: 'Dezember 2025'
  }
];

const aboutPoints = [
  {
    icon: Award,
    title: 'Über 35 Jahre Erfahrung',
    description: 'Seit 1988 sind wir Ihr zuverlässiger Partner für Nissan, Mazda, Omoda und Jaecoo in Magdeburg.'
  },
  {
    icon: Users,
    title: 'Familienbetrieb mit Herz',
    description: 'Persönliche Beratung und individuelle Betreuung stehen bei uns an erster Stelle.'
  },
  {
    icon: ShieldCheck,
    title: 'Autorisierter Händler',
    description: 'Offizieller Nissan, Mazda, Omoda & Jaecoo Vertragshändler mit geschultem Fachpersonal.'
  }
];

export const TrustSection = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Über uns */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Warum Autohaus Wilke?
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Ihr Vertrauen ist unser Antrieb – seit über drei Jahrzehnten
          </p>
        </div>

        {/* About Points */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {aboutPoints.map((point, index) => (
            <Card
              key={index}
              className="p-8 text-center border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-2xl bg-white"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                <point.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{point.title}</h3>
              <p className="text-gray-600 leading-relaxed">{point.description}</p>
            </Card>
          ))}
        </div>

        {/* Partner Logos */}
        <div className="mb-20">
          <p className="text-center text-gray-500 text-sm uppercase tracking-wider mb-8">Offizieller Partner von</p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10">
            <a
              href="https://wilke.haendler-nissan.de/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl px-6 py-5 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105"
              data-testid="partner-logo-nissan"
            >
              <img
                src="/nissan-logo-2020.svg"
                alt="Nissan Partner - Autohaus Wilke"
                className="h-14 w-auto object-contain"
              />
            </a>
            <a
              href="https://www.mazda-autohaus-wilke-magdeburg.de/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl px-6 py-5 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105"
              data-testid="partner-logo-mazda"
            >
              <img
                src="/mazda-logo-2024.svg"
                alt="Mazda Partner - Autohaus Wilke"
                className="h-14 w-auto object-contain"
              />
            </a>
            <a
              href="https://oj.autohaus-wilke.de"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-xl px-6 py-5 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105"
              data-testid="partner-logo-omoda-jaecoo"
            >
              <img
                src="/omoda-jaecoo-logo.webp"
                alt="Omoda & Jaecoo Partner - Autohaus Wilke"
                className="h-8 md:h-10 w-auto max-w-[220px] object-contain"
              />
            </a>
          </div>
          {/* Standort-Hinweis Omoda & Jaecoo */}
          <div className="mt-8 max-w-3xl mx-auto flex items-start gap-3 px-5 py-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-900" data-testid="omoda-jaecoo-location-notice">
            <svg className="flex-shrink-0 w-5 h-5 mt-0.5 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            <p className="text-sm md:text-[15px] leading-relaxed">
              <strong>Standort-Info:</strong> Omoda &amp; Jaecoo werden vorerst am Standort <strong>Silberbergweg 30</strong> geführt – <strong>ab 29. August 2026</strong> ziehen die neuen Marken in den <strong>Florapark Magdeburg</strong> um.
            </p>
          </div>
        </div>

        {/* Kundenbewertungen */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <span className="text-2xl font-bold text-gray-900 ml-2">4.9</span>
            <span className="text-gray-500">/ 5</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Das sagen unsere Kunden</h3>
          <p className="text-gray-600">Basierend auf über 200 Bewertungen</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {reviews.map((review) => (
            <Card
              key={review.id}
              className="p-6 border-0 shadow-lg rounded-2xl bg-white hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <div className="relative mb-4">
                <Quote className="absolute -top-2 -left-2 w-8 h-8 text-red-100" />
                <p className="text-gray-700 leading-relaxed pl-4 italic">
                  "{review.text}"
                </p>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <span className="font-semibold text-gray-900">{review.name}</span>
                <span className="text-sm text-gray-500">{review.date}</span>
              </div>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="https://www.google.com/search?q=autohaus+wilke+magdeburg+bewertungen"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold transition-colors"
          >
            Alle Bewertungen auf Google ansehen
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};
